import { Card, HTMLTable, Overlay, Tabs, Button } from "@blueprintjs/core";
import { Observer, observer, useLocalObservable } from "mobx-react";
import { openMetaPanel, useCommand } from "./hotkeys";
import React from "react";
import { DriverContext } from "./driver";
import { Cell, CellRenderer, Column, Table2 } from "@blueprintjs/table";
import { Channel } from "./channels";
import {
  getLoggers,
  levels,
  LogLevelDesc,
  LogLevel,
  getLogger,
} from "loglevel";
import { useUpdate } from "ahooks";

const log = getLogger("meta-panel");

export const MetaPanel = observer(function () {
  const state = useLocalObservable(() => ({
    isOpen: false,
  }));

  useCommand(openMetaPanel, () => {
    state.isOpen = !state.isOpen;
  });

  function close() {
    state.isOpen = false;
    log.debug("Meta panel closed");
  }

  return (
    <Overlay
      isOpen={state.isOpen}
      onClose={close}
      canEscapeKeyClose={true}
      canOutsideClickClose={true}
    >
      <div className="inset-4 fixed" style={{ color: "#1C2127" }}>
        <Card className="m-0 inset-0 absolute" interactive={false}>
          <Tabs large={true} className="size-full flex flex-col">
            <Tabs.Tab
              panelClassName="flex-1 overflow-hidden"
              id="Channels"
              title="Channels"
              panel={<ChannelPanel />}
            />
            <Tabs.Tab
              panelClassName="flex-1 overflow-hidden"
              id="logger"
              title="Loggers"
              panel={<LoggerPanel />}
            />
          </Tabs>
        </Card>
      </div>
    </Overlay>
  );
});
export const dateTimeFormat = {
  format: (timestamp: number | Date) => {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toISOString().replace("T", " ").replace(".000Z", "");
  },
};

const ChannelPanel = observer(function () {
  const driver = React.useContext(DriverContext);

  const channels = driver.channels;

  const channelCount = channels?.getAllChannels().length ?? 0;

  function getCell(
    render: (channel: Channel) => React.ReactElement | null
  ): CellRenderer {
    function r(i: number, c: number) {
      const channel = channels?.getChannel(i);
      if (!channel) return <Cell />;
      return (
        <Cell>
          <Observer render={() => render(channel)} />
        </Cell>
      );
    }
    return r;
  }

  return (
    <Table2
      className="size-full"
      numRows={channelCount}
      defaultColumnWidth={150}
    >
      <Column
        name="index"
        cellRenderer={getCell((c) => (
          <span>{c.index}</span>
        ))}
      />
      <Column
        name="mode"
        cellRenderer={getCell((c) => (
          <span>{c.mode}</span>
        ))}
      />
      <Column
        name="hasContent"
        cellRenderer={getCell((c) => (
          <span>{c.hasContent ? "yes" : "no"}</span>
        ))}
      />
      <Column
        name="latestTime"
        cellRenderer={getCell((c) => (
          <span>
            {c.latestTime ? dateTimeFormat.format(c.latestTime) : "n/a"}
          </span>
        ))}
      />
      <Column
        name="resetTime"
        cellRenderer={getCell((c) => (
          <span>{dateTimeFormat.format(c.resetTime)}</span>
        ))}
      />
    </Table2>
  );
});

const LoggerPanel = function () {
  const loggers = getLoggers();
  const update = useUpdate();

  const resetAllLoggers = () => {
    Object.values(loggers).forEach((logger) => {
      logger.resetLevel();
    });
    update();
  };

  return (
    <div>
      <div className="mb-4">
        <Button
          intent="primary"
          onClick={resetAllLoggers}
          text="Reset All Loggers to Default"
        />
      </div>
      <HTMLTable striped={true} bordered={true} condensed={true}>
        <thead>
          <tr>
            <th>Logger</th>
            <th>Level</th>
            <th className="w-12">Actions</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(loggers).map((k) => (
            <tr key={k} className="group">
              <td>{k}</td>
              {((l) => {
                const level = l.getLevel();
                const levelName =
                  Object.entries(levels).find(
                    ([_, value]) => value === level
                  )?.[0] ?? "UNKNOWN";
                return (
                  <>
                    <td>
                      <select
                        value={levelName}
                        onChange={(e) => {
                          l.setLevel(e.target.value as LogLevelDesc);
                          update();
                        }}
                      >
                        {Object.keys(levels).map((levelKey) => (
                          <option key={levelKey} value={levelKey}>
                            {levelKey}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="flex items-center justify-center">
                      <Button
                        small
                        minimal
                        icon="reset"
                        className="invisible group-hover:visible"
                        onClick={() => {
                          l.resetLevel();
                          update();
                        }}
                      />
                    </td>
                  </>
                );
              })(loggers[k])}
            </tr>
          ))}
        </tbody>
      </HTMLTable>
    </div>
  );
};
